<template>
  <div class="transactions-list">
    <!-- Owner info alert -->
    <v-alert v-if="summary && summary.label" type="info" dense outlined class="mb-4">
      <div class="d-flex align-center">
        <v-icon left>{{ ownerIcon }}</v-icon>
        <div class="flex-grow-1">
          <div class="text-body-2">
            <strong>{{ summary.label }}</strong>
          </div>
          <div v-if="ownerInfo && ownerInfo.email" class="text-caption text--secondary">
            {{ ownerInfo.email }}
          </div>
          <!-- Bank account details for organizations/customers -->
          <div v-if="ownerInfo && (ownerInfo.type === 'organization' || ownerInfo.type === 'customer')" class="text-caption text--secondary mt-1">
            <span v-if="ownerInfo.details.last4">IBAN: •••• {{ ownerInfo.details.last4 }}</span>
            <span v-if="ownerInfo.details.bankName" class="ml-2">{{ ownerInfo.details.bankName }}</span>
            <span v-if="ownerInfo.details.country" class="ml-2">{{ ownerInfo.details.country }}</span>
          </div>
          <!-- Onboarding status badge for organizations/customers -->
          <div v-if="ownerInfo && ownerInfo.details && ownerInfo.details.onboardingStatus" class="mt-1">
            <v-chip x-small :color="onboardingStatusColor(ownerInfo.details.onboardingStatus)" dark>
              {{ ownerInfo.details.onboardingStatus }}
            </v-chip>
          </div>
        </div>
      </div>
    </v-alert>

    <!-- Header with refresh button -->
    <div class="d-flex justify-space-between align-center mb-3">
      <h3 class="text-h6">{{ $t('payment-method-ui.transactions') }}</h3>
      <v-btn
        small
        icon
        @click="reload"
        :loading="loading"
        :title="$t('payment-method-ui.reload')"
      >
        <v-icon small>mdi-refresh</v-icon>
      </v-btn>
    </div>

    <!-- Error -->
    <v-alert v-if="error" type="error" dense outlined class="mb-4">
      {{ error }}
    </v-alert>

    <!-- Transactions Table -->
    <v-data-table
      :headers="headers"
      :items="transactions"
      :loading="loading"
      :items-per-page="-1"
      hide-default-footer
    >
      <template v-slot:item.id="{ item }">
        <div>
          <div class="d-flex align-center gap-1">
            <v-chip x-small :color="transactionTypeColor(item.type)" dark>
              <v-icon x-small left>{{ transactionTypeIcon(item.type) }}</v-icon>
              {{ item.type }}
            </v-chip>
            <span class="text-caption text--secondary">{{ item.id }}</span>
          </div>
          <div v-if="item.description" class="text-caption text--secondary mt-1">
            {{ item.description }}
          </div>
        </div>
      </template>

      <!-- Payment Method / Destination -->
      <template v-slot:item.paymentMethod="{ item }">
        <div v-if="item.type === 'transfer'">
          <div class="text-body-2">
            <v-chip x-small color="purple" outlined>
              <v-icon x-small left>mdi-bank-transfer</v-icon>
              Transfer
            </v-chip>
          </div>
          <div class="text-caption text--secondary mt-1">
            <div v-if="item.sourceType">{{ $t('payment-method-ui.source') }}: {{ item.sourceType }}</div>
            <div v-if="item.destination">{{ $t('payment-method-ui.destination') }}: {{ item.destination }}</div>
          </div>
        </div>
        <div v-else-if="item.paymentMethodDetails">
          <div class="text-body-2">
            {{ formatPaymentMethod(item.paymentMethodDetails) }}
          </div>
        </div>
        <div v-else-if="item.type === 'payout'">
          <div class="text-body-2">
            <v-chip x-small color="green" outlined>
              <v-icon x-small left>mdi-bank</v-icon>
              Payout
            </v-chip>
          </div>
          <div class="text-caption text--secondary mt-1">
            <div v-if="item.method">{{ $t('payment-method-ui.method') }}: {{ item.method }}</div>
            <div v-if="item.destination && item.destination.last4">•••• {{ item.destination.last4 }}</div>
          </div>
        </div>
      </template>

      <!-- Amount -->
      <template v-slot:item.amount="{ item }">
        <div>
          <strong>{{ formatAmount(item.amount, item.currency) }}</strong>
          <div v-if="item.fees" class="text-caption text--secondary">
            {{ $t('payment-method-ui.fees') }}: {{ formatAmount(item.fees.total, item.currency) }}
          </div>
          <div v-if="item.netAmount" class="text-caption success--text">
            {{ $t('payment-method-ui.net') }}: {{ formatAmount(item.netAmount, item.currency) }}
          </div>
          <div v-if="item.amountRefunded > 0" class="text-caption error--text">
            {{ $t('payment-method-ui.refunded') }}: {{ formatAmount(item.amountRefunded, item.currency) }}
          </div>
          <!-- Transfer reversals -->
          <div v-if="item.type === 'transfer' && item.amountReversed > 0" class="text-caption error--text">
            {{ $t('payment-method-ui.reversed') }}: {{ formatAmount(item.amountReversed, item.currency) }}
          </div>
        </div>
      </template>

      <!-- Status -->
      <template v-slot:item.status="{ item }">
        <div class="d-flex flex-column gap-1">
          <v-chip x-small :color="statusColor(item.status)" dark>
            {{ item.status }}
          </v-chip>
          <v-chip v-if="item.paid" x-small color="green" outlined>
            {{ $t('payment-method-ui.paid') }}
          </v-chip>
          <!-- Transfer reversed indicator -->
          <v-chip v-if="item.type === 'transfer' && item.reversed" x-small color="red" outlined>
            <v-icon x-small left>mdi-undo-variant</v-icon>
            {{ $t('payment-method-ui.reversed') }}
          </v-chip>
        </div>
      </template>

      <!-- Created -->
      <template v-slot:item.createdAt="{ item }">
        <div>{{ formatDate(item.createdAt) }}</div>
        <div v-if="item.arrivalDate" class="text-caption text--secondary">
          {{ $t('payment-method-ui.arrival') }}: {{ formatDate(item.arrivalDate) }}
        </div>
      </template>

      <!-- Actions -->
      <template v-slot:item.actions="{ item }">
        <div class="d-flex flex-wrap gap-1">
          <!-- Authenticate Payment (requires_action) -->
          <v-btn
            v-if="canManagePayments && item.status === 'requires_action' && item.type === 'payment'"
            x-small
            icon
            color="primary"
            @click="handleAuthenticate(item)"
            :title="$t('payment-method-ui.authenticate')"
          >
            <v-icon x-small>mdi-shield-lock</v-icon>
          </v-btn>

          <!-- Receipt -->
          <v-btn
            v-if="item.receiptUrl"
            x-small
            icon
            :href="item.receiptUrl"
            target="_blank"
            :title="$t('payment-method-ui.view-receipt')"
          >
            <v-icon x-small>mdi-receipt-text-outline</v-icon>
          </v-btn>

          <!-- Cancel Payment -->
          <v-btn
            v-if="item.capabilities?.canCancel && item.type === 'payment'"
            x-small
            icon
            color="warning"
            @click="handleCancelPayment(item)"
            :title="$t('payment-method-ui.cancel')"
          >
            <v-icon x-small>mdi-cancel</v-icon>
          </v-btn>

          <!-- Capture Payment -->
          <v-btn
            v-if="canManagePayments && item.capabilities?.canCapture"
            x-small
            icon
            color="success"
            @click="openCaptureDialog(item)"
            :title="$t('payment-method-ui.capture')"
          >
            <v-icon x-small>mdi-cash-check</v-icon>
          </v-btn>

          <!-- Refund Payment -->
          <v-btn
            v-if="item.capabilities?.canRefund"
            x-small
            icon
            color="error"
            @click="handleRefundPayment(item)"
            :title="$t('payment-method-ui.refund')"
          >
            <v-icon x-small>mdi-cash-refund</v-icon>
          </v-btn>

          <!-- Cancel Payout -->
          <v-btn
            v-if="canManagePayments && item.capabilities?.canCancel && item.type === 'payout'"
            x-small
            icon
            color="warning"
            @click="handleCancelPayout(item)"
            :title="$t('payment-method-ui.cancel-payout')"
          >
            <v-icon x-small>mdi-cancel</v-icon>
          </v-btn>

          <!-- Reverse Transfer -->
          <v-btn
            v-if="canManagePayments && item.capabilities?.canReverse && item.type === 'transfer'"
            x-small
            icon
            color="error"
            @click="handleReverseTransfer(item)"
            :title="$t('payment-method-ui.reverse-transfer')"
          >
            <v-icon x-small>mdi-undo-variant</v-icon>
          </v-btn>
        </div>
      </template>
    </v-data-table>

    <!-- Custom Pagination Footer -->
    <div v-if="transactions.length" class="d-flex justify-space-between align-center pa-4">
      <div class="d-flex align-center">
        <span class="text-body-2 mr-3">{{ $t('payment-method-ui.items-per-page') }}:</span>
        <v-select
          v-model="itemsPerPage"
          :items="[10, 20, 50, 100]"
          dense
          outlined
          hide-details
          style="max-width: 100px"
          @change="handleItemsPerPageChange"
        ></v-select>
      </div>

      <div class="d-flex align-center">
        <span class="text-body-2 mr-4">
          {{ $t('payment-method-ui.page-info', { page: currentPage, count: transactions.length }) }}
        </span>

        <v-btn
          :disabled="currentPage === 1 || loading"
          @click="goToPrevious"
          icon
          small
        >
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>

        <v-btn
          :disabled="!pagination.hasMore || loading"
          @click="goToNext"
          icon
          small
        >
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
      </div>
    </div>

    <!-- Last transaction summary -->
    <v-alert v-if="lastTransaction" type="success" dense outlined class="mt-4">
      <div class="text-caption">
        <strong>{{ $t('payment-method-ui.last-transaction') }}:</strong>
        {{ formatAmount(lastTransaction.amount, lastTransaction.currency) }}
        - {{ lastTransaction.status }}
        ({{ formatDate(lastTransaction.createdAt) }})
      </div>
    </v-alert>

    <!-- Payment Auth Dialog -->
    <payment-auth-dialog
      v-model="authDialog"
      :payment-response="authPaymentResponse"
      @success="handleAuthSuccess"
      @error="handleAuthError"
      @close="handleAuthClose"
    />

    <!-- Capture Dialog -->
    <v-dialog v-model="captureDialog" max-width="500px">
      <v-card>
        <v-card-title>
          <v-icon left color="success">mdi-cash-check</v-icon>
          {{ $t('payment-method-ui.capture-payment') }}
        </v-card-title>

        <v-card-text>
          <div class="mb-4">
            <div class="text-body-2 mb-2">{{ $t('payment-method-ui.transaction-id') }}: <strong>{{ captureTransaction?.id }}</strong></div>
            <div class="text-body-2 mb-2">
              {{ $t('payment-method-ui.amount-capturable') }}:
              <strong>{{ formatAmount(captureTransaction?.amountCapturable || 0, captureTransaction?.currency) }}</strong>
            </div>
          </div>

          <v-text-field
            v-model.number="captureAmount"
            :label="$t('payment-method-ui.amount-to-capture')"
            type="number"
            :min="0"
            :max="maxCaptureAmount"
            outlined
            dense
            :suffix="captureTransaction?.currency?.toUpperCase() || 'EUR'"
            :hint="$t('payment-method-ui.amount-in-units')"
            persistent-hint
            :rules="[
              v => v >= 0 || $t('payment-method-ui.amount-must-be-positive'),
              v => v <= maxCaptureAmount || $t('payment-method-ui.amount-exceeds-max', { max: formatAmount(captureTransaction?.amountCapturable || 0, captureTransaction?.currency) })
            ]"
          >
            <template v-slot:append-outer>
              <v-btn
                small
                text
                color="primary"
                @click="captureAmount = maxCaptureAmount"
              >
                {{ $t('payment-method-ui.capture-all') }}
              </v-btn>
            </template>
          </v-text-field>

          <v-alert type="info" dense outlined class="mt-2">
            {{ $t('payment-method-ui.capture-hint') }}
          </v-alert>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn small text @click="captureDialog = false">
            {{ $t('cancel') }}
          </v-btn>
          <v-btn
            color="success"
            small
            @click="handleCapturePayment"
            :disabled="!isValidCaptureAmount"
          >
            {{ $t('payment-method-ui.capture') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import PaymentMethodRepository from '@repository/PaymentMethodRepository'
import PaymentAuthDialog from '@elements/PaymentAuthDialog.vue'

export default {
  name: 'PaymentTransactionList',

  components: {
    PaymentAuthDialog
  },

  props: {
    organizationId: {
      type: String,
      default: null
    },
    customerId: {
      type: String,
      default: null
    },
    paymentMethodId: {
      type: String,
      default: null
    },
    beneficiaryId: {
      type: String,
      default: null
    },
    autoLoad: {
      type: Boolean,
      default: true
    },
    pageSize: {
      type: Number,
      default: 20
    }
  },

  data() {
    return {
      loading: false,
      error: null,
      transactions: [],
      captureDialog: false,
      captureTransaction: null,
      captureAmount: 0,
      authDialog: false,
      authPaymentResponse: null,
      itemsPerPage: 10,
      currentPage: 1,
      pagination: {
        hasMore: false,
        nextPage: null,
        limit: 20
      },
      pageTokens: {}, // Map: numéro de page -> token { 2: "page_abc", 3: "page_def" }
      ownerInfo: null, // Informations du propriétaire (owner)
      lastTransaction: null, // Dernière transaction
      summary: null // Résumé des transactions
    }
  },

  computed: {
    // La finance (PAYMENT_MANAGE) a toutes les actions ; le SAV seulement Annuler et Rembourser un paiement de client.
    canManagePayments() {
      return (this.currentUser?.permissions || []).includes('PAYMENT_MANAGE')
    },

    headers() {
      return [
        { text: this.$t('payment-method-ui.transaction-id'), value: 'id', sortable: false },
        { text: this.$t('payment-method-ui.payment-method'), value: 'paymentMethod', sortable: false },
        { text: this.$t('amount'), value: 'amount', sortable: false },
        { text: this.$t('status'), value: 'status', sortable: false },
        { text: this.$t('created'), value: 'createdAt', sortable: false },
        {  value: 'actions', sortable: false, align: 'center' }
      ]
    },

    maxCaptureAmount() {
      if (!this.captureTransaction?.amountCapturable) return 0
      // Convert from cents to units (divide by 100)
      return this.captureTransaction.amountCapturable / 100
    },

    isValidCaptureAmount() {
      return this.captureAmount > 0 && this.captureAmount <= this.maxCaptureAmount
    },

    ownerIcon() {
      if (!this.ownerInfo) return 'mdi-information'

      const icons = {
        payment_method: 'mdi-credit-card',
        customer: 'mdi-account',
        organization: 'mdi-office-building'
      }

      return icons[this.ownerInfo.type] || 'mdi-information'
    }
  },

  mounted() {
    if (this.autoLoad) {
      this.loadTransactions()
    }
  },

  watch: {
    organizationId() {
      this.resetAndLoad()
    },
    customerId() {
      this.resetAndLoad()
    },
    paymentMethodId() {
      this.resetAndLoad()
    },
    beneficiaryId() {
      this.resetAndLoad()
    }
  },

  methods: {
    onboardingStatusColor(status) {
      const colors = {
        active: 'green',
        pending: 'yellow',
        requirements_due: 'orange',
        restricted: 'red',
        disabled: 'grey'
      }
      return colors[status] || 'grey'
    },

    transactionTypeColor(type) {
      const colors = {
        payment: 'green',   // Payment = argent entrant = vert
        payout: 'blue',     // Payout = virement sortant = bleu
        transfer: 'purple'  // Transfer = transfert interne = violet
      }
      return colors[type] || 'grey'
    },

    transactionTypeIcon(type) {
      const icons = {
        payment: 'mdi-arrow-down',    // Flèche vers le bas pour argent entrant
        payout: 'mdi-arrow-up',       // Flèche vers le haut pour virement sortant
        transfer: 'mdi-bank-transfer' // Icône de transfert
      }
      return icons[type] || 'mdi-help-circle'
    },

    reload() {
      // Reset to first page
      this.currentPage = 1
      this.pageTokens = {}
      this.loadTransactions()
    },

    resetAndLoad() {
      this.transactions = []
      this.pageTokens = {}
      this.currentPage = 1
      this.pagination = {
        hasMore: false,
        nextPage: null,
        limit: this.itemsPerPage
      }
      if (this.autoLoad) {
        this.loadTransactions()
      }
    },

    handleItemsPerPageChange() {
      // Reset tout quand on change le nombre d'items
      this.resetAndLoad()
    },

    goToNext() {
      if (this.pagination.hasMore && this.pagination.nextPage) {
        // Incrémenter la page AVANT de stocker le token
        this.currentPage++
        // Stocker le token pour la nouvelle page
        this.pageTokens[this.currentPage] = this.pagination.nextPage
        // Charger
        this.loadTransactions()
      }
    },

    goToPrevious() {
      if (this.currentPage > 1) {
        // Décrémenter la page
        this.currentPage--
        // Charger (le token est déjà dans pageTokens si nécessaire)
        this.loadTransactions()
      }
    },

    async loadTransactions() {
      this.loading = true
      this.error = null

      try {
        // Build params object with only defined values
        const params = {
          limit: this.itemsPerPage
        }

        // Si on est sur la page 1, pas de token
        // Sinon, utiliser le token stocké
        if (this.currentPage > 1) {
          const pageToken = this.pageTokens[this.currentPage]
          if (pageToken) {
            params.page = pageToken
          }
        }

        if (this.paymentMethodId) {
          params.paymentMethodId = this.paymentMethodId
        } else if (this.beneficiaryId) {
          params.beneficiaryId = this.beneficiaryId
        } else if (this.organizationId) {
          params.organizationId = this.organizationId
        } else if (this.customerId) {
          params.customerId = this.customerId
        } else {
          throw new Error('Either organizationId, customerId, paymentMethodId or beneficiaryId must be provided')
        }

        const result = await PaymentMethodRepository.getTransactions(params)

        this.transactions = result.items || []
        this.pagination = result.pagination || {
          hasMore: false,
          nextPage: null,
          limit: this.itemsPerPage
        }

        // Stocker les métadonnées enrichies du backend
        this.ownerInfo = result.owner || null
        this.lastTransaction = result.summary?.lastTransaction || null
        this.summary = result.summary || null

        // Le token de nextPage sera stocké lors du clic sur "Suivant"
        // Pas besoin de le stocker ici
      } catch (err) {
        this.error = err?.response?.data?.message || err?.message || 'Error loading transactions'
      } finally {
        this.loading = false
      }
    },

    formatAmount(amount, currency) {
      if (!amount) return '0.00'
      // Stripe amounts are in cents
      const amountInUnits = amount / 100
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: currency?.toUpperCase() || 'EUR'
      }).format(amountInUnits)
    },

    formatDate(val) {
      if (!val) return ''
      try {
        return new Date(val).toLocaleString()
      } catch (e) {
        return String(val)
      }
    },

    statusColor(status) {
      const colors = {
        succeeded: 'green',
        processing: 'blue',
        requires_action: 'orange',
        requires_payment_method: 'orange',
        requires_capture: 'orange',
        requires_confirmation: 'orange',
        canceled: 'grey',
        failed: 'red',
        pending: 'orange',
        paid: 'green',
        in_transit: 'blue'
      }
      return colors[status] || 'grey'
    },

    formatPaymentMethod(details) {
      if (!details) return ''

      if (details.type === 'card' && details.card) {
        const brand = details.card.brand?.toUpperCase() || 'CARD'
        const last4 = details.card.last4 || '****'
        return `${brand} •••• ${last4}`
      }

      if (details.type === 'sepa_debit' && details.sepa_debit) {
        const last4 = details.sepa_debit.last4 || '****'
        return `SEPA •••• ${last4}`
      }

      if (details.type === 'bancontact') {
        return 'Bancontact'
      }

      return details.type || 'Unknown'
    },

    async handleCancelPayment(transaction) {
      const reasons = [
        { text: this.$t('payment-method-ui.cancel-reasons.requested-by-customer'), value: 'requested_by_customer' },
        { text: this.$t('payment-method-ui.cancel-reasons.duplicate'), value: 'duplicate' },
        { text: this.$t('payment-method-ui.cancel-reasons.fraudulent'), value: 'fraudulent' },
        { text: this.$t('payment-method-ui.cancel-reasons.abandoned'), value: 'abandoned' }
      ]

      const confirmed = await this.$dialog?.confirm({
        text: this.$t('payment-method-ui.confirm-cancel-payment', { id: transaction.id }),
        title: this.$t('payment-method-ui.cancel-payment')
      })

      if (!confirmed) return

      try {
        this.loading = true
        await PaymentMethodRepository.cancelPayment(transaction.id)
        this.$dialog?.notify?.success && this.$dialog.notify.success(this.$t('payment-method-ui.payment-canceled'))
        await this.loadTransactions()
      } catch (err) {
        this.notifyError(err)
      } finally {
        this.loading = false
      }
    },

    openCaptureDialog(transaction) {
      this.captureTransaction = transaction
      // Set default to max capturable amount (in units, not cents)
      this.captureAmount = this.maxCaptureAmount
      this.captureDialog = true
    },

    async handleCapturePayment() {
      if (!this.isValidCaptureAmount) return

      try {
        this.loading = true
        this.captureDialog = false

        // Convert amount back to cents for API
        const amountInCents = Math.round(this.captureAmount * 100)

        await PaymentMethodRepository.capturePayment(this.captureTransaction.id, {
          amount_to_capture: amountInCents
        })

        this.$dialog?.notify?.success && this.$dialog.notify.success(this.$t('payment-method-ui.payment-captured'))
        await this.loadTransactions()
      } catch (err) {
        this.notifyError(err)
      } finally {
        this.loading = false
        this.captureTransaction = null
        this.captureAmount = 0
      }
    },

    async handleRefundPayment(transaction) {
      const maxRefund = transaction.amountRefundable || 0

      const confirmed = await this.$dialog?.confirm({
        text: this.$t('payment-method-ui.confirm-refund-payment', {
          id: transaction.id,
          amount: this.formatAmount(maxRefund, transaction.currency)
        }),
        title: this.$t('payment-method-ui.refund-payment')
      })

      if (!confirmed) return

      try {
        this.loading = true
        await PaymentMethodRepository.refundPayment(transaction.id)
        this.$dialog?.notify?.success && this.$dialog.notify.success(this.$t('payment-method-ui.payment-refunded'))
        await this.loadTransactions()
      } catch (err) {
        this.notifyError(err)
      } finally {
        this.loading = false
      }
    },

    async handleCancelPayout(transaction) {
      const confirmed = await this.$dialog?.confirm({
        text: this.$t('payment-method-ui.confirm-cancel-payout', { id: transaction.id }),
        title: this.$t('payment-method-ui.cancel-payout')
      })

      if (!confirmed) return

      try {
        this.loading = true
        await PaymentMethodRepository.cancelPayout(transaction.id)
        this.$dialog?.notify?.success && this.$dialog.notify.success(this.$t('payment-method-ui.payout-canceled'))
        await this.loadTransactions()
      } catch (err) {
        this.notifyError(err)
      } finally {
        this.loading = false
      }
    },

    async handleReverseTransfer(transaction) {
      const maxReverse = transaction.amount - transaction.amountReversed

      const confirmed = await this.$dialog?.confirm({
        text: this.$t('payment-method-ui.confirm-reverse-transfer', {
          id: transaction.id,
          amount: this.formatAmount(maxReverse, transaction.currency)
        }),
        title: this.$t('payment-method-ui.reverse-transfer')
      })

      if (!confirmed) return

      try {
        this.loading = true
        await PaymentMethodRepository.reverseTransfer(transaction.id, {
          amount: maxReverse
        })
        this.$dialog?.notify?.success && this.$dialog.notify.success(this.$t('payment-method-ui.transfer-reversed'))
        await this.loadTransactions()
      } catch (err) {
        this.notifyError(err)
      } finally {
        this.loading = false
      }
    },

    async handleAuthenticate(transaction) {
      // Si la transaction a déjà un clientSecret (données complètes), l'utiliser directement
      const clientSecret = transaction.clientSecret || transaction.client_secret
      const nextAction = transaction.nextAction || transaction.next_action

      if (clientSecret) {
        this.authPaymentResponse = {
          id: transaction.id,
          status: transaction.status,
          amount: transaction.amount,
          currency: transaction.currency,
          client_secret: clientSecret,
          next_action: nextAction
        }
        this.authDialog = true
        return
      }

      // Sinon, récupérer le PaymentIntent complet depuis le backend
      try {
        this.loading = true

        // Récupérer le PaymentIntent complet avec le client_secret
        // Le backend vérifie automatiquement les permissions de l'utilisateur
        // (pas besoin d'organizationId, le backend identifie le propriétaire automatiquement)
        const paymentIntent = await PaymentMethodRepository.getPaymentIntent(transaction.id)

        // Vérifier qu'on a bien le clientSecret (backend retourne en camelCase)
        if (!paymentIntent.clientSecret) {
          throw new Error('Payment intent does not have a clientSecret. It may no longer require authentication.')
        }

        // Normaliser le format pour PaymentAuthDialog (qui attend client_secret en snake_case)
        this.authPaymentResponse = {
          ...paymentIntent,
          client_secret: paymentIntent.clientSecret,
          next_action: paymentIntent.nextAction
        }
        this.authDialog = true

      } catch (err) {
        this.notifyError(err)
      } finally {
        this.loading = false
      }
    },

    handleAuthSuccess(paymentIntent) {
      this.$dialog?.notify?.success && this.$dialog.notify.success(
        this.$t('payment-method-ui.authentication-success')
      )

      // Recharger les transactions pour voir le nouveau statut
      this.loadTransactions()

      // Fermer le dialog
      this.authDialog = false
    },

    handleAuthError(error) {
      this.notifyError(error?.message || 'payment-method-ui.authentication-failed')

      // Le dialog reste ouvert pour permettre de réessayer ou de voir l'erreur
    },

    handleAuthClose() {
      this.authDialog = false
      this.authPaymentResponse = null
    }
  }
}
</script>

<style scoped>
.transactions-list {
  /* Add any custom styles here */
}
</style>
